import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, FormInput } from 'components/common';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import AsyncSelect from 'react-select/async';
import { useForm, Controller } from 'react-hook-form';
import { activeEmployeesDebounce } from 'helpers/employeeOptionHelper';
import PropTypes from 'prop-types';
import { FaRegSave, FaUserTie, FaBan } from 'react-icons/fa';
import { createRequestUserAccount } from 'redux/request/userAccountSlice';
import { getDepartmentBySite } from 'redux/organization/siteSlice';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { adjustToNoon } from 'helpers/utils';
import PhoneInput from 'react-phone-input-2';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { REQUEST_USER_ACCOUNT_PURPOSE } from 'app_config';
const RequestUserAccountForm = () => {
  const { data: user } = useSelector((state) => state.profile_user);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      purpose: [],
    },
    mode: 'onChange',
  });

  const [countryCode, setCountryCode] = useState('VN');

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const siteId = user.site.id;

  useEffect(() => {
    setValue('start_date', adjustToNoon(new Date()));
  }, [setValue]);

  const defaultDepartmentOption = useMemo(
    () =>
      user && user.department
        ? { value: user.department.id, label: user.department.name }
        : null,
    [user]
  );

  const [departmentOption, setDepartmentOption] = useState(defaultDepartmentOption);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await dispatch(getDepartmentBySite(siteId)).unwrap();

        const options = response.rows
          .map((item) => item.attributes)
          .map((item) => ({
            value: item.id,
            label: item.name,
          }));

        setDepartmentOption(options);
      } catch (error) {
        toast.error(error.message);
      }
    };

    fetchDepartments();
  }, [dispatch, siteId]);

  const onSubmit = async (data) => {
    try {
      const response = await dispatch(createRequestUserAccount(data)).unwrap();
      const payload = response.data;

      if (payload && payload.id !== undefined) {
        navigateConfirmationRoute(payload.id);
      }
    } catch (error) {
      const msgs = error?.error;
      const message = Array.isArray(msgs) ? msgs.join('\n') : (error?.message || 'An error occurred.');
      toast.error(message, { style: { whiteSpace: 'pre-line' } });
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const loadManagerOptions = (inputValue, callback) => {
    activeEmployeesDebounce(inputValue, callback);
  };

  const navigateConfirmationRoute = (id) => {
    navigate(`/request/user-accounts/${id}/confirmation`);
  };

  const validatePhoneNumber = (number) => {
    if (number === undefined || number === '' || number === null) return undefined;

    if (!isValidPhoneNumber(number.toString(), countryCode)) {
      return `Invalid phone number with country code ${countryCode}.`;
    }
    return true;
  };

  return (
    <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-100 tw-text-sm">
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b tw-border-slate-100">
          <h5 className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-slate-800 tw-m-0">
            <FaUserTie className="tw-text-slate-500" />
            New Request User Account
          </h5>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-md tw-bg-slate-100 tw-text-slate-700 hover:tw-bg-slate-200 tw-border-0 tw-cursor-pointer tw-transition-colors"
          >
            Back
          </button>
        </div>

        {/* Body */}
        <div className="tw-px-6 tw-py-5 tw-space-y-4">
          <Alert
            type="warning"
            message="This function allows users to conveniently submit all required account information so that the IT department can create user accounts on the Portal. Please complete all necessary details and submit them for processing."
          />

          {/* Full Name */}
          <div className="tw-grid tw-grid-cols-[200px_1fr] tw-items-start tw-gap-4">
            <label className="tw-text-sm tw-font-medium tw-text-slate-700 tw-text-right tw-pt-2">
              Full name<span className="tw-text-red-500 tw-ml-1">*</span>
            </label>
            <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-max-w-xl">
              <div>
                <FormInput
                  type="text"
                  placeholder="First name"
                  error={errors.first_name?.message}
                  {...register('first_name', {
                    required: 'First name must be required.',
                  })}
                />
              </div>
              <div>
                <FormInput
                  type="text"
                  placeholder="Last name"
                  error={errors.last_name?.message}
                  {...register('last_name', {
                    required: 'Last name must be required.',
                  })}
                />
              </div>
            </div>
          </div>

          {/* Code */}
          <div className="tw-grid tw-grid-cols-[200px_1fr] tw-items-start tw-gap-4">
            <label className="tw-text-sm tw-font-medium tw-text-slate-700 tw-text-right tw-pt-2">
              Code
            </label>
            <div className="tw-max-w-xl">
              <FormInput
                type="text"
                placeholder="3 characters as policy"
                error={errors.name?.message}
                {...register('name', {
                  maxLength: { value: 10, message: 'Code must not exceed 10 characters.' },
                })}
              />
            </div>
          </div>

          {/* Title */}
          <div className="tw-grid tw-grid-cols-[200px_1fr] tw-items-start tw-gap-4">
            <label className="tw-text-sm tw-font-medium tw-text-slate-700 tw-text-right tw-pt-2">
              Title<span className="tw-text-red-500 tw-ml-1">*</span>
            </label>
            <div className="tw-max-w-xl">
              <FormInput
                type="text"
                placeholder="Title"
                error={errors.title?.message}
                {...register('title', {
                  required: 'Title must be required.',
                })}
              />
            </div>
          </div>

          {/* Phone Number */}
          <div className="tw-grid tw-grid-cols-[200px_1fr] tw-items-start tw-gap-4">
            <label className="tw-text-sm tw-font-medium tw-text-slate-700 tw-text-right tw-pt-2">
              Phone number<span className="tw-text-red-500 tw-ml-1">*</span>
            </label>
            <div className="tw-max-w-xl">
              <Controller
                control={control}
                name="phone_number"
                rules={{ validate: validatePhoneNumber }}
                render={({ field: { ref, ...field } }) => (
                  <PhoneInput
                    {...field}
                    inputProps={{ ref }}
                    country="vn"
                    autoFormat={true}
                    countryCodeEditable={false}
                    enableSearch={true}
                    disableSearchIcon={true}
                    inputStyle={{ width: '100%' }}
                    searchStyle={{ width: '95%' }}
                    onChange={(value, country) => {
                      field.onChange(value);
                      setCountryCode(country.countryCode.toUpperCase());
                    }}
                  />
                )}
              />
              {errors.phone_number && (
                <p className="tw-text-xs tw-text-red-600 tw-font-medium tw-mt-1">
                  {errors.phone_number.message}
                </p>
              )}
            </div>
          </div>

          {/* Gender */}
          <div className="tw-grid tw-grid-cols-[200px_1fr] tw-items-start tw-gap-4 tw-mt-4">
            <label className="tw-text-sm tw-font-medium tw-text-slate-700 tw-text-right tw-pt-2">
              Gender<span className="tw-text-red-500 tw-ml-1">*</span>
            </label>
            <div className="tw-max-w-xl">
              <Controller
                control={control}
                name="gender"
                defaultValue={false}
                rules={{ required: 'Gender must be required.' }}
                render={({ field }) => (
                  <div className="tw-flex tw-items-center tw-gap-4 tw-pt-2">
                    {['Male', 'Female'].map((item) => (
                      <label
                        key={item}
                        className="tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-sm tw-text-slate-700"
                      >
                        <input
                          type="radio"
                          checked={field.value === item}
                          onChange={() => setValue('gender', item)}
                          className="tw-cursor-pointer"
                        />
                        {item}
                      </label>
                    ))}
                  </div>
                )}
              />
              {errors.gender && (
                <p className="tw-text-xs tw-text-red-600 tw-font-medium tw-mt-1">
                  {errors.gender.message}
                </p>
              )}
            </div>
          </div>

          {/* Onboard Date */}
          <div className="tw-grid tw-grid-cols-[200px_1fr] tw-items-start tw-gap-4 tw-mt-4">
            <label className="tw-text-sm tw-font-medium tw-text-slate-700 tw-text-right tw-pt-2">
              Onboard date
            </label>
            <div className="tw-max-w-xl">
              <Controller
                control={control}
                name="onboard_date"
                render={({ field }) => (
                  <DatePicker
                    selected={field.value}
                    onChange={(date) => field.onChange(adjustToNoon(date))}
                    className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-solid tw-border-slate-300 tw-rounded-md tw-bg-white tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                    placeholderText="DD-MM-YYYY"
                    dateFormat="dd-MM-yyyy"
                    fixedHeight
                    isClearable={true}
                  />
                )}
              />
            </div>
          </div>

          {/* Line Manager */}
          <div className="tw-grid tw-grid-cols-[200px_1fr] tw-items-start tw-gap-4">
            <label className="tw-text-sm tw-font-medium tw-text-slate-700 tw-text-right tw-pt-2">
              Line Manager
            </label>
            <div className="tw-max-w-xl">
              <Controller
                control={control}
                name="manager"
                render={({ field }) => (
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={loadManagerOptions}
                    closeMenuOnSelect={true}
                    placeholder="<Select manager>"
                    classNamePrefix="react-select"
                    className="tw-w-full"
                    value={
                      field.value
                        ? { value: field.value.id, label: field.value.name }
                        : null
                    }
                    onChange={(selectedOption) =>
                      field.onChange(
                        selectedOption
                          ? { id: selectedOption.value, name: selectedOption.label }
                          : null
                      )
                    }
                  />
                )}
              />
              {errors.manager && (
                <p className="tw-text-xs tw-text-red-600 tw-font-medium tw-mt-1">
                  {errors.manager.message}
                </p>
              )}
            </div>
          </div>

          {/* Department */}
          <div className="tw-grid tw-grid-cols-[200px_1fr] tw-items-start tw-gap-4">
            <label className="tw-text-sm tw-font-medium tw-text-slate-700 tw-text-right tw-pt-2">
              Department
            </label>
            <div className="tw-max-w-xl">
              <Controller
                control={control}
                name="department_id"
                render={({ field }) => (
                  <Select
                    isClearable
                    options={departmentOption}
                    value={
                      departmentOption &&
                      departmentOption.find((opt) => opt.value === field.value)
                    }
                    onChange={(selectedOption) => {
                      field.onChange(selectedOption ? selectedOption.value : null);
                    }}
                    placeholder="Select..."
                    classNamePrefix="react-select"
                    className="tw-w-full"
                  />
                )}
              />
            </div>
          </div>

          {/* Purpose */}
          <div className="tw-grid tw-grid-cols-[200px_1fr] tw-items-start tw-gap-4 tw-mt-4">
            <label className="tw-text-sm tw-font-medium tw-text-slate-700 tw-text-right tw-pt-2">
              Purpose<span className="tw-text-red-500 tw-ml-1">*</span>
            </label>
            <div className="tw-max-w-xl">
              <Controller
                control={control}
                name="purpose"
                rules={{
                  validate: (value) =>
                    (value && value.length > 0) || 'Purpose must be required.',
                }}
                render={({ field }) => (
                  <div className="tw-flex tw-flex-col tw-gap-2 tw-pt-2">
                    {REQUEST_USER_ACCOUNT_PURPOSE.map((item) => (
                      <label
                        key={item}
                        className="tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-sm tw-text-slate-700"
                      >
                        <input
                          type="checkbox"
                          value={item}
                          checked={Array.isArray(field.value) && field.value.includes(item)}
                          onChange={() => {
                            const currentValue = Array.isArray(field.value)
                              ? field.value
                              : [];
                            if (currentValue.includes(item)) {
                              field.onChange(currentValue.filter((i) => i !== item));
                            } else {
                              field.onChange([...currentValue, item]);
                            }
                          }}
                          className="tw-cursor-pointer"
                        />
                        {item}
                      </label>
                    ))}
                  </div>
                )}
              />
              {errors.purpose && (
                <p className="tw-text-xs tw-text-red-600 tw-font-medium tw-mt-1">
                  {errors.purpose.message}
                </p>
              )}
            </div>
          </div>

          {/* Remark */}
          <div className="tw-grid tw-grid-cols-[200px_1fr] tw-items-start tw-gap-4 tw-mt-4">
            <label className="tw-text-sm tw-font-medium tw-text-slate-700 tw-text-right tw-pt-2">
              Remark
            </label>
            <div className="tw-max-w-xl">
              <FormInput
                type="textarea"
                placeholder="Remark"
                rows={10}
                {...register('remark')}
                onChange={undefined}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-3 tw-px-6 tw-py-4 tw-border-t tw-border-slate-100 tw-bg-slate-50 tw-rounded-b-2xl">
          <Button type="submit" variant="primary" size="md">
            <FaRegSave />
            Submit
          </Button>
          <Button type="button" variant="danger" size="md" onClick={handleCancel}>
            <FaBan />
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

RequestUserAccountForm.propTypes = {
  employee: PropTypes.shape({
    id: PropTypes.number,
    site_id: PropTypes.number,
    site: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    department: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    parent: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  }),
};

export default RequestUserAccountForm;
